import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Check, Copy } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { IntegrationGuide } from '@/components/settings/IntegrationGuide';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/queryKeys';
import { toast } from '@/hooks/use-toast';
import type { ApiKey, ApiKeyKind, RotatedKey } from '@/lib/types';

async function fetchKeys(): Promise<ApiKey[]> {
  const { data } = await api.get<ApiKey[]>('/v1/keys');
  return data;
}

function maskedToken(key: ApiKey): string {
  return `${key.prefix}_••••${key.lastFour}`;
}

export default function SettingsKeys() {
  const qc = useQueryClient();
  const keys = useQuery({ queryKey: queryKeys.keys, queryFn: fetchKeys });
  const [revealed, setRevealed] = useState<RotatedKey | null>(null);

  const rotate = useMutation({
    mutationFn: async (kind: ApiKeyKind) => {
      const { data } = await api.post<RotatedKey>('/v1/keys/rotate', { kind });
      return data;
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: queryKeys.keys });
      setRevealed(data);
      toast({ title: `${data.kind === 'secret' ? 'Secret' : 'Publishable'} key rotated` });
    },
    onError: () =>
      toast({ title: 'Could not rotate key', variant: 'destructive' }),
  });

  const groups: { kind: ApiKeyKind; title: string; description: string }[] = [
    {
      kind: 'publishable',
      title: 'Publishable key',
      description:
        'Ships in your mobile app. Write-only for click + install events; bound to your declared bundle IDs.',
    },
    {
      kind: 'secret',
      title: 'Secret key',
      description:
        'Server-side only. Full read/write tenant access. Shown once on rotation — store it somewhere safe.',
    },
  ];

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-semibold">API keys</h1>
        <p className="text-sm text-muted-foreground">
          Two keys per tenant — one for your mobile binary, one for your server.
        </p>
      </header>

      {keys.isLoading && (
        <div className="space-y-3">
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-32 w-full" />
        </div>
      )}
      {keys.isError && <p className="text-sm text-destructive">Could not load keys.</p>}

      {keys.data && (
        <div className="space-y-4">
          {groups.map(({ kind, title, description }) => {
            const active = keys.data?.find((k) => k.kind === kind && !k.revokedAt);
            return (
              <Card key={kind}>
                <CardHeader className="flex flex-row items-start justify-between gap-4">
                  <div>
                    <CardTitle>{title}</CardTitle>
                    <CardDescription>{description}</CardDescription>
                  </div>
                  <Button
                    variant="outline"
                    onClick={() => rotate.mutate(kind)}
                    disabled={rotate.isPending}
                  >
                    {rotate.isPending && rotate.variables === kind ? 'Rotating…' : 'Rotate'}
                  </Button>
                </CardHeader>
                <CardContent>
                  {active ? (
                    <div className="flex items-center justify-between gap-3 rounded-md border bg-muted/30 p-3">
                      <code className="font-mono text-sm">{maskedToken(active)}</code>
                      <span className="text-xs text-muted-foreground">
                        Created {new Date(active.createdAt).toLocaleDateString()}
                      </span>
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">No active key.</p>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <IntegrationGuide />

      <RevealKeyDialog
        rotated={revealed}
        onClose={() => setRevealed(null)}
      />
    </div>
  );
}

function RevealKeyDialog({
  rotated,
  onClose,
}: {
  rotated: RotatedKey | null;
  onClose: () => void;
}) {
  const open = !!rotated;
  const [copied, setCopied] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const isSecret = rotated?.kind === 'secret';

  // Reset the gate every time a new key is revealed so a prior session can't
  // pre-confirm dismissal of a fresh secret.
  useEffect(() => {
    if (open) {
      setCopied(false);
      setConfirmed(false);
    }
  }, [open, rotated?.id]);

  async function copy() {
    if (!rotated) return;
    try {
      await navigator.clipboard.writeText(rotated.token);
      setCopied(true);
      toast({ title: 'Copied to clipboard' });
    } catch {
      toast({ title: 'Copy failed', variant: 'destructive' });
    }
  }

  // Secret keys are shown once (spec §5.3). Block accidental dismissal:
  // ignore backdrop clicks + Escape so the customer can't lose the key.
  return (
    <Dialog open={open} onOpenChange={(o) => !o && isSecret ? undefined : onClose()}>
      <DialogContent
        onPointerDownOutside={(e) => {
          if (isSecret) e.preventDefault();
        }}
        onEscapeKeyDown={(e) => {
          if (isSecret) e.preventDefault();
        }}
      >
        <DialogHeader>
          <DialogTitle>Your new {rotated?.kind} key</DialogTitle>
          <DialogDescription>
            {isSecret
              ? 'This secret key is shown ONCE. We do not store the plaintext; if you lose it you must rotate again.'
              : 'Copy this publishable key into your mobile app. Shown once.'}
          </DialogDescription>
        </DialogHeader>

        {isSecret && (
          <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-100">
            <AlertTriangle className="mt-0.5 size-4 shrink-0" aria-hidden />
            <p>
              Store this somewhere safe — a password manager, your secrets
              manager, or your server config. We never display it again.
            </p>
          </div>
        )}

        <div className="space-y-2">
          <code className="block break-all rounded-md bg-muted p-3 font-mono text-sm">
            {rotated?.token}
          </code>
        </div>

        {isSecret && (
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={confirmed}
              onChange={(e) => setConfirmed(e.target.checked)}
              className="size-4 rounded border-input"
            />
            <span>I have saved this key in a secure location.</span>
          </label>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={copy}>
            {copied ? <Check /> : <Copy />}
            {copied ? 'Copied' : 'Copy'}
          </Button>
          <Button
            onClick={onClose}
            disabled={isSecret && !confirmed}
          >
            {isSecret ? 'I have it saved' : 'Close'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
